import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { Notification } from '@model/notification';
import { appDatabase } from '@shared/database/app-database';
import { BaseService } from '@shared/service/base.service';
import { sortNotificationsByDateTimeSent } from '@util/notification-util';
import { catchError, forkJoin, from, map, Observable, tap } from 'rxjs';

import { PushNotificationService } from './push-notification.service';

type NotificationResponse = Omit<Notification, 'isRead' | 'isDeleted'>;

@Injectable({ providedIn: 'root' })
export class NotificationService extends BaseService {
  private _notifications = signal<Notification[] | undefined>(undefined);
  private _notificationTable = appDatabase.notification;

  private pushNotificationService = inject(PushNotificationService);

  notifications = computed<Notification[]>(() => {
    const notifications = this._notifications() ?? [];

    return notifications.filter(
      notification => notification.isDeleted === false,
    );
  });

  unreadNotifications = computed<Notification[]>(() => {
    return this.notifications().filter(
      notification => notification.isRead === false,
    );
  });

  hasUnreadNotifications = computed<boolean>(
    () => this.unreadNotifications().length > 0,
  );

  hasNotifications = computed<boolean>(() => this.notifications().length > 0);

  latestNotification = computed<Notification | undefined>(() => {
    return this.notifications().find(notication => notication.isRead === false);
  });

  constructor() {
    super('notifications');

    effect(() => {
      const notifications = this._notifications();

      void this.processTableItems(notifications);
    });

    effect(() => {
      // We listen to a new pushNotification but won't process the result
      // The new list of notifications will be taken from the backend
      const _pushNotification = this.pushNotificationService.pushNotification();

      if (_pushNotification) {
        untracked(() => this.processNotifications().subscribe());
      }
    });
  }

  private async processTableItems(
    notifications?: Notification[],
  ): Promise<void> {
    // No processing when notifications is undefined
    // Otherwise the stored data is deleted on app init
    if (notifications !== undefined) {
      // Check the stored notifications in the table against the new list, bulkDelete by the found deleted uuids
      const storedNotifications = await this._notificationTable.toArray();
      const deletedNotifications = storedNotifications.filter(
        storedNotification =>
          notifications.find(
            notification => notification.uuid === storedNotification.uuid,
          ) === undefined,
      );
      const deletedNotificationUuids = deletedNotifications.map(
        storedNotification => storedNotification.uuid,
      );
      await this._notificationTable.bulkDelete(deletedNotificationUuids);

      await this._notificationTable.bulkPut(notifications);
    }
  }

  private getAll(): Observable<NotificationResponse[]> {
    return this.httpClient.get<NotificationResponse[]>(this.baseUrl).pipe(
      catchError(() => {
        // On error of Notifications call the flow needs to continue.
        // Will return an empty array so the content from the database is used in the app
        return from([]);
      }),
    );
  }

  private processNotifications(): Observable<void> {
    // Get all stored and backend notifications and set/compose the _notifications signal
    return forkJoin([
      from(this._notificationTable.toArray()),
      this.getAll(),
    ]).pipe(
      map(([storedNotifications, backendNotifications]) => {
        // Only process the stored notifications which are also received from the backend
        const filteredStoredNotifications = storedNotifications.filter(
          storedNotification =>
            backendNotifications.find(
              backendNotification =>
                backendNotification.uuid === storedNotification.uuid,
            ) !== undefined,
        );

        // Create an array of added notifications, filter the stored notifications and add fields isRead and isDeleted
        const addedNotifications: Notification[] = backendNotifications
          .filter(
            backendNotification =>
              storedNotifications.find(
                storedNotification =>
                  storedNotification.uuid === backendNotification.uuid,
              ) === undefined,
          )
          .map(backendNotification => ({
            ...backendNotification,
            isRead: false,
            isDeleted: false,
          }));

        // Combine a new list of filtered/stored and added notifications
        const notificationList = [
          ...addedNotifications,
          ...filteredStoredNotifications,
        ];

        return sortNotificationsByDateTimeSent(notificationList);
      }),
      tap(notifications => this._notifications.set(notifications)),
      map(() => undefined),
    );
  }

  init(): Observable<void> {
    return this.processNotifications();
  }

  markAsRead(readNotification: Notification): void {
    this._notifications.update(notifications => {
      if (notifications) {
        return notifications.map(notification => ({
          ...notification,
          isRead:
            notification.uuid === readNotification.uuid
              ? true
              : notification.isRead,
        }));
      }
    });
  }

  markAsDeleted(deletedNotification: Notification): void {
    this._notifications.update(notifications => {
      if (notifications) {
        return notifications.map(notification => ({
          ...notification,
          isDeleted:
            notification.uuid === deletedNotification.uuid
              ? true
              : notification.isDeleted,
          isRead:
            notification.uuid === deletedNotification.uuid
              ? true
              : notification.isRead,
        }));
      }
    });
  }
}
